import React, { useEffect, useState, useMemo, useContext } from "react";
import { Modal, Table, Input, Checkbox, Button, message, Spin, Typography } from "antd";
import { platformApi } from "../../../../api";
import { ContextAPI } from "../../../../ContextAPI";

const { TextArea } = Input;
const { Title, Text } = Typography;

const TEAL_BTN = {
  backgroundColor: "#0d9488",
  borderColor: "#0d9488",
  color: "#ffffff",
  fontSize: "14px",
};

const FinalInspectionModal = ({
  open,
  onCancel,
  jobOrder,
  onSuccess,
  readOnly = false,
}) => {
  const { loginCredintials } = useContext(ContextAPI);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [redoing, setRedoing] = useState(false);
  const [complaintItems, setComplaintItems] = useState([]);
  const [finalComment, setFinalComment] = useState("");
  const [isEditMode, setIsEditMode] = useState(true);

  const status = String(jobOrder?.jobStatus || "").trim();

  const isWashingOrBeyond =
    status === "Washing" ||
    status === "Washing and Final Inspection" ||
    status === "Proforma Invoice" ||
    status === "Payment Completed" ||
    status === "Ready for Delivery" ||
    status === "Feedback Call" ||
    status === "Closed";

  const canSave = useMemo(() => {
    return (
      complaintItems.length > 0 &&
      complaintItems.every(
        (item) =>
          item.verified === true ||
          (typeof item.comment === "string" && item.comment.trim().length > 0)
      )
    );
  }, [complaintItems]);

  // Role Access: TRANSACTIONMASTER → job_card_redo → create
  const canRedoByRole = Boolean(
    loginCredintials?.roleAccess?.some(
      (each) => each.subModule === "job_card_redo" && each.access?.create
    )
  );

  // Show Redo when editable FI + Department Role Access (Job Card Redo Option → Create)
  const showRedo = !readOnly && isEditMode && !isWashingOrBeyond && canRedoByRole;

  useEffect(() => {
    if (open && jobOrder?.id) {
      // In Washing or beyond, or when readOnly is specified, start in view mode (read-only)
      setIsEditMode(!readOnly && !isWashingOrBeyond);
      fetchInspectionData();
    } else if (!open) {
      setComplaintItems([]);
      setFinalComment("");
      setIsEditMode(true);
    }
  }, [open, jobOrder, readOnly]);

  const fetchInspectionData = async () => {
    setLoading(true);
    try {
      // First, get raw complaints from jobOrder
      let rawComplaints = [];
      if (Array.isArray(jobOrder.complaint)) {
        rawComplaints = jobOrder.complaint;
      } else if (Array.isArray(jobOrder.complaintList)) {
        rawComplaints = jobOrder.complaintList;
      } else if (jobOrder.complaints && Array.isArray(jobOrder.complaints)) {
        rawComplaints = jobOrder.complaints;
      }

      // Try fetching saved inspection record from backend
      let savedInspection = null;
      try {
        const res = await platformApi.get(`/api/finalInspection/${jobOrder.id}`);
        if (res?.data?.code === 200 && res?.data?.response?.data) {
          savedInspection = res.data.response.data;
        } else if (res?.data?.data) {
          savedInspection = res.data.data;
        }
      } catch (err) {
        // Backend endpoint might return 404 if not yet created, which is normal
        console.log("No saved final inspection found yet, initializing from complaints");
      }

      if (savedInspection) {
        setFinalComment(savedInspection.finalComment || "");
        if (Array.isArray(savedInspection.items) && savedInspection.items.length > 0) {
          // Map to find complaint text using raw complaints or complaint relation
          setComplaintItems(
            savedInspection.items.map((item, idx) => {
              // 1. Try finding matching complaint in rawComplaints by ID
              const matchingRaw = rawComplaints.find(
                (rc) => rc && (rc.id === item.complaint || rc.id === item.complaintId)
              );
              const rawText = matchingRaw
                ? (typeof matchingRaw === "string" ? matchingRaw : matchingRaw.complaint || matchingRaw.description)
                : null;

              // 2. Try nested JobVehicleComplaint
              const relText = item.JobVehicleComplaint?.complaint;

              // 3. Fallback to complaintText if it's not a cuid
              const isCuid = typeof item.complaint === "string" && item.complaint.startsWith("c") && item.complaint.length === 25;
              const textFallback = item.complaintText || (!isCuid ? item.complaint : null) || "-";

              return {
                key: item.id || `insp-item-${idx}`,
                id: item.id,
                complaintId: item.complaint || item.complaintId || (matchingRaw ? matchingRaw.id : null),
                complaint: rawText || relText || textFallback,
                comment: item.comment || "",
                verified: Boolean(item.verified || item.isVerified),
              };
            })
          );
        } else {
          buildDefaultItems(rawComplaints);
        }
      } else {
        buildDefaultItems(rawComplaints);
      }
    } catch (err) {
      console.error("Error fetching inspection data", err);
      message.error("Failed to load final inspection details");
    } finally {
      setLoading(false);
    }
  };

  const buildDefaultItems = (rawComplaints) => {
    if (rawComplaints.length === 0) {
      setComplaintItems([]);
      return;
    }
    const items = rawComplaints.map((c, idx) => {
      const text = typeof c === "string" ? c : c.complaint || c.description || c.desc || "-";
      return {
        key: c.id || `raw-${idx}`,
        complaintId: c.id || null,
        complaint: text,
        comment: "",
        verified: false,
      };
    });
    setComplaintItems(items);
  };

  const handleCommentChange = (index, value) => {
    const updated = [...complaintItems];
    updated[index].comment = value;
    setComplaintItems(updated);
  };

  const handleVerifiedChange = (index, checked) => {
    const updated = [...complaintItems];
    updated[index].verified = checked;
    setComplaintItems(updated);
  };

  const handleSave = async () => {
    if (!canSave) {
      message.warning("Please verify or add a comment for each complaint before saving.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        jobOrderId: jobOrder.id,
        finalComment: finalComment || "",
        items: complaintItems.map((item) => ({
          complaintId: item.complaintId,
          complaint: item.complaint,
          verified: item.verified,
          comment: item.comment || "",
        })),
        isCompleted: true,
      };

      const res = await platformApi.post(`/api/finalInspection/${jobOrder.id}`, payload);
      const apiCode = res?.data?.response?.code ?? res?.data?.code;
      const apiMessage =
        res?.data?.response?.message ||
        res?.data?.message ||
        "Failed to save final inspection";

      if (apiCode === 200) {
        message.success("Final Inspection completed! Status changed to Washing.");
        if (onSuccess) onSuccess("Washing");
        onCancel();
      } else if (apiCode === 400) {
        message.warning(apiMessage);
      } else {
        message.error(apiMessage);
      }
    } catch (err) {
      console.error("Error saving final inspection", err);
      const errMsg =
        err?.response?.data?.response?.message ||
        err?.response?.data?.message ||
        "Failed to save final inspection";
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const performRedo = async () => {
    setRedoing(true);
    try {
      const res = await platformApi.post(`/api/jobOrder/redoFinalInspection/${jobOrder.id}`);
      const apiCode = res?.data?.response?.code ?? res?.data?.code;
      const apiMessage =
        res?.data?.response?.message ||
        res?.data?.message ||
        "Failed to redo Final Inspection";

      if (apiCode === 200) {
        message.success(
          apiMessage ||
            "Job sent back for rework. Use Start Work to continue the timer."
        );
        if (onSuccess) onSuccess("Work In Progress");
        onCancel();
      } else {
        message.error(apiMessage);
      }
    } catch (err) {
      console.error("Error redoing Final Inspection", err);
      const errMsg =
        err?.response?.data?.response?.message ||
        err?.response?.data?.message ||
        "Failed to redo Final Inspection";
      message.error(errMsg);
    } finally {
      setRedoing(false);
    }
  };

  const handleRedoClick = () => {
    Modal.confirm({
      title: "Send this job back for rework?",
      content: "Complaints will be reset and the work timer will start automatically.",
      okText: "Yes, Redo",
      cancelText: "Cancel",
      onOk: () => performRedo(),
    });
  };

  const columns = [
    {
      title: <span style={{ fontSize: "15px", fontWeight: 700 }}>S.No</span>,
      key: "sno",
      width: 65,
      align: "center",
      render: (_, __, index) => <strong style={{ fontSize: "16px" }}>{index + 1}</strong>,
    },
    {
      title: <span style={{ fontSize: "15px", fontWeight: 700 }}>Complaint</span>,
      dataIndex: "complaint",
      key: "complaint",
      width: "35%",
      render: (text) => <span style={{ fontWeight: 600, fontSize: "15px", color: "#1f2937" }}>{text}</span>,
    },
    {
      title: <span style={{ fontSize: "15px", fontWeight: 700 }}>Comments</span>,
      dataIndex: "comment",
      key: "comment",
      width: "55%",
      render: (text, record, index) => (
        <TextArea
          rows={3}
          style={{ fontSize: "15px", lineHeight: 1.5 }}
          placeholder={isEditMode ? "Enter comment / reason..." : "-"}
          value={text}
          disabled={!isEditMode}
          onChange={(e) => handleCommentChange(index, e.target.value)}
        />
      ),
    },
    {
      title: <span style={{ fontSize: "15px", fontWeight: 700 }}>Verified</span>,
      dataIndex: "verified",
      key: "verified",
      width: 95,
      align: "center",
      render: (checked, record, index) => (
        <Checkbox
          checked={checked}
          disabled={!isEditMode}
          style={{ transform: "scale(1.3)" }}
          onChange={(e) => handleVerifiedChange(index, e.target.checked)}
        />
      ),
    },
  ];

  const vehicleRegNo = jobOrder?.vehicle?.registerNo || jobOrder?.vehicle?.registrationNo || "-";

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0, fontSize: "19px" }}>
            Final Inspection {readOnly ? <span style={{ fontSize: "14px", fontWeight: "normal", color: "#6b7280" }}>(View Only)</span> : null}
          </Title>
          <Text type="secondary" style={{ fontSize: "14px" }}>
            Job No: <strong>{jobOrder?.jobNo || "-"}</strong> &nbsp;|&nbsp; Reg No:{" "}
            <strong>{vehicleRegNo}</strong>
          </Text>
        </div>
      }
      visible={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={920}
    >
      <style>{`
        .final-inspection-modal-content .ant-table-thead > tr > th {
          font-size: 15px !important;
          font-weight: 700 !important;
          color: #1f2937 !important;
          padding: 12px 14px !important;
        }
        .final-inspection-modal-content .ant-table-tbody > tr > td {
          font-size: 15px !important;
          padding: 12px 14px !important;
        }
        .final-inspection-modal-content textarea.ant-input {
          font-size: 15px !important;
          line-height: 1.5 !important;
        }
      `}</style>
      <Spin spinning={loading || saving || redoing}>
        <div className="final-inspection-modal-content" style={{ marginTop: "10px" }}>
          <Table
            bordered
            size="middle"
            pagination={false}
            columns={columns}
            dataSource={complaintItems}
            locale={{ emptyText: <span style={{ fontSize: "15px" }}>No complaints found for this Job Card.</span> }}
            rowKey={(record) => record.key || record.id}
          />

          <div style={{ marginTop: "18px" }}>
            <label style={{ fontWeight: 700, display: "block", marginBottom: "8px", fontSize: "15px", color: "#1f2937" }}>
              Final Comment (General)
            </label>
            <TextArea
              rows={3}
              style={{ fontSize: "15px", lineHeight: 1.5 }}
              placeholder={isEditMode ? "Enter general final comment (optional)..." : "-"}
              value={finalComment}
              disabled={!isEditMode}
              onChange={(e) => setFinalComment(e.target.value)}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              marginTop: "24px",
              flexWrap: "wrap",
            }}
          >
            <div>
              {showRedo ? (
                <Button
                  size="middle"
                  loading={redoing}
                  onClick={handleRedoClick}
                  style={TEAL_BTN}
                >
                  Redo
                </Button>
              ) : (
                <span />
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Button size="middle" style={{ fontSize: "14px" }} onClick={onCancel}>Close</Button>

              {!readOnly && (
                !isEditMode && isWashingOrBeyond ? (
                  <Button size="middle" style={{ ...TEAL_BTN }} type="primary" onClick={() => setIsEditMode(true)}>
                    Modify
                  </Button>
                ) : isEditMode ? (
                  <Button
                    size="middle"
                    type="primary"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!canSave}
                    style={
                      canSave
                        ? TEAL_BTN
                        : { fontSize: "14px" }
                    }
                  >
                    Save
                  </Button>
                ) : null
              )}
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default FinalInspectionModal;
